import type { Experiment, Variant } from 'types';
import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';

/**
 * Picking the "before" and "after" variant for an experiment.
 *
 * Two assumptions broke the implementation page against real data:
 *
 *  - controlVariantId is frequently null. Every Deployed/Ended experiment on
 *    the website this was first tested against had it unset. Matching with
 *    `v.id === experiment.controlVariantId` then finds nothing, and the inverse
 *    test `v.id !== experiment.controlVariantId` matches *every* variant, so
 *    "first non-control" returned the control itself.
 *  - `deployed` is only set on a variant once an experiment is deployed. Ended
 *    experiments have it unset on every variant, so keying off it alone left
 *    nothing selected.
 *
 * Control falls back to the variant named 'Original', which is the convention
 * used elsewhere (see getSignificance and the combination report handler).
 */

export const variantHasCode = (variant: Variant | null | undefined): boolean =>
  !!variant && (!!variant.globalJavascript || !!variant.globalCSS || !!variant.changes?.length);

const screenshotCount = (variant: Variant | null | undefined): number => variant?.screenshots?.length ?? 0;

type ResolvedVariants = {
  control: Variant | null;
  /** The variant to show as "after": deployed if there is one, else the most informative. */
  treatment: Variant | null;
};

export const resolveVariants = (experiment: Experiment): ResolvedVariants => {
  const variants = experiment.variants ?? [];
  if (variants.length === 0) return { control: null, treatment: null };

  const control =
    variants.find((v) => experiment.controlVariantId != null && v.id === experiment.controlVariantId) ??
    variants.find((v) => v.name === 'Original') ??
    null;

  const others = control ? variants.filter((v) => v.id !== control.id) : variants;

  // Prefer a deployed variant; otherwise the one that actually has something to
  // show, so the row is not blank just because the first variant is empty.
  const treatment =
    others.find((v) => !!v.deployed) ??
    others.find((v) => variantHasCode(v)) ??
    others.find((v) => screenshotCount(v) > 0) ??
    others[0] ??
    null;

  return { control, treatment };
};

export type ExperimentWinner = {
  variant: Variant | null;
  /**
   * True when something actually decided this: a rollout, or a statistically resolved
   * win on one of the goals. False means the row is showing the variant that was
   * tested, which is not the same claim and must not be dressed as one.
   */
  confirmed: boolean;
  /**
   * Which of the two decided it. Deployment is a human decision and outranks the
   * numbers: on a deployed experiment the live variant is the one a reader needs,
   * whatever the statistics later say.
   */
  source: 'deployment' | 'result' | 'tested';
};

export const resolveWinner = (experiment: ExperimentWithVerdict): ExperimentWinner => {
  const variants = experiment.variants ?? [];
  const deployed = variants.find((v) => !!v.deployed);
  if (deployed) return { variant: deployed, confirmed: true, source: 'deployment' };

  // A resolved win names its own variant. Before this the page picked whichever variant
  // carried code or screenshots and captioned it "winning variant", which named the
  // wrong arm on any multi-variant experiment and named one at all on experiments that
  // had no winner.
  const winningId = experiment.verdict?.winner?.variantId;
  const won = winningId != null ? variants.find((v) => v.id === winningId) : undefined;
  if (won) return { variant: won, confirmed: true, source: 'result' };

  // Nothing deployed and nothing won: fall back to whichever variant the page is
  // showing code and screenshots for, but do not call it a winner.
  return { variant: resolveVariants(experiment).treatment, confirmed: false, source: 'tested' };
};

/**
 * The variant whose code the Code column should open. Falls back to the control
 * so an experiment whose only code lives on the original is still inspectable.
 */
export const resolveCodeVariant = (experiment: Experiment): Variant | null => {
  const { control, treatment } = resolveVariants(experiment);
  if (variantHasCode(treatment)) return treatment;
  if (variantHasCode(control)) return control;
  return (experiment.variants ?? []).find((v) => variantHasCode(v)) ?? null;
};
